﻿<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { CategoryRecord, Id, ImportOptions, ImportProgress } from '../types/catalog'
import { formatBytes } from '../utils/format'
import { parseTags } from '../utils/text'
import AppDialog from './AppDialog.vue'
import AppIcon from './AppIcon.vue'
import CategorySelect from './CategorySelect.vue'

const props = defineProps<{
  open: boolean
  categories: CategoryRecord[]
  defaultCategoryId: Id | null
  initialFiles: File[]
  importing: boolean
  progress: ImportProgress | null
}>()

const emit = defineEmits<{
  close: []
  submit: [files: File[], options: ImportOptions]
}>()

const files = ref<File[]>([])
const categoryId = ref<Id | null>(null)
const description = ref('')
const tagsText = ref('')
const input = ref<HTMLInputElement | null>(null)

const totalSize = computed(() => files.value.reduce((sum, file) => sum + file.size, 0))

watch(() => props.open, (open) => {
  if (!open) return
  files.value = [...props.initialFiles]
  categoryId.value = props.defaultCategoryId ?? props.categories[0]?.id ?? null
  description.value = ''
  tagsText.value = ''
}, { immediate: true })

function addFiles(selected: FileList | null): void {
  if (!selected) return
  const map = new Map(files.value.map((file) => [`${file.name}-${file.size}-${file.lastModified}`, file]))
  for (const file of Array.from(selected)) {
    if (file.type.startsWith('image/')) map.set(`${file.name}-${file.size}-${file.lastModified}`, file)
  }
  files.value = [...map.values()]
  if (input.value) input.value.value = ''
}

function removeFile(index: number): void {
  files.value.splice(index, 1)
}

function submit(): void {
  if (!categoryId.value || !files.value.length) return
  emit('submit', files.value, {
    categoryId: categoryId.value,
    description: description.value,
    tags: parseTags(tagsText.value)
  })
}
</script>

<template>
  <AppDialog
    :open="open"
    title="Import images"
    wide
    full-screen-mobile
    :close-disabled="importing"
    @close="emit('close')"
  >
    <template #eyebrow>New content</template>

    <div class="import-layout">
      <div class="import-dropzone" @click="input?.click()">
        <input ref="input" class="visually-hidden" type="file" accept="image/*" multiple @change="addFiles(($event.target as HTMLInputElement).files)" />
        <div class="import-dropzone-icon"><AppIcon name="upload" :size="28" /></div>
        <strong>{{ files.length ? `${files.length} images selected` : 'Select images' }}</strong>
        <span>JPEG, PNG, WebP, AVIF e altri browser-decodable formats</span>
      </div>

      <div v-if="files.length" class="selected-files">
        <div v-for="(file, index) in files.slice(0, 8)" :key="`${file.name}-${file.lastModified}`" class="selected-file-row">
          <span>{{ file.name }}</span>
          <button type="button" class="mini-icon-button" :aria-label="`Rimuovi ${file.name}`" :disabled="importing" @click="removeFile(index)">
            <AppIcon name="close" :size="15" />
          </button>
        </div>
        <p v-if="files.length > 8" class="field-hint">e altre {{ files.length - 8 }} immagini</p>
        <p class="field-hint">Totale: {{ formatBytes(totalSize) }}</p>
      </div>

      <div class="form-grid">
        <CategorySelect v-model="categoryId" :categories="categories" required />
        <label class="field field--full">
          <span>Common description <small>optional</small></span>
          <textarea v-model="description" rows="3" placeholder="Add a description to the imported images" />
        </label>
        <label class="field field--full">
          <span>Common tags <small>comma separated</small></span>
          <input v-model="tagsText" type="text" placeholder="travel, documents, work" />
        </label>
      </div>

      <div v-if="importing && progress" class="progress-panel">
        <div class="progress-copy">
          <strong>Processing {{ progress.current }} di {{ progress.total }}</strong>
          <span>{{ progress.fileName || 'Finishing…' }}</span>
        </div>
        <progress :value="progress.current" :max="progress.total" />
        <p>{{ progress.imported }} imported · {{ progress.failed }} failed</p>
      </div>
    </div>

    <template #footer>
      <button type="button" class="button button--ghost" :disabled="importing" @click="emit('close')">Cancel</button>
      <button type="button" class="button button--primary" :disabled="importing || !files.length || !categoryId" @click="submit">
        <span v-if="importing" class="spinner spinner--small" />
        <AppIcon v-else name="upload" :size="18" />
        {{ importing ? 'Importing…' : `Import ${files.length || ''}` }}
      </button>
    </template>
  </AppDialog>
</template>
